import { motion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

/** Shared shell for PAQVERO's static content pages — dark theme, amber accents. */
function ContentPage({ eyebrow, title, intro, sections = [], faqs = [], cards = [], contact = null, testid }) {
  return (
    <div className="min-h-screen pb-24 pt-28" data-testid={testid}>
      <div className="mx-auto max-w-4xl px-6 lg:px-12">
        <motion.div variants={reveal} initial="hidden" animate="visible">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">{eyebrow}</p>
          <h1 className="font-display text-4xl font-medium tracking-tight sm:text-5xl">{title}</h1>
          {intro && <p className="mt-5 max-w-2xl text-base leading-relaxed text-2 lg:text-lg">{intro}</p>}
        </motion.div>

        {cards.length > 0 && (
          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {cards.map((c) => (
              <Link key={c.title} to={c.to} state={c.state} data-testid={c.testid}
                className="group rounded-2xl border border-line bg-hover p-6 transition-colors duration-300 hover:border-amber-500/40 hoverable">
                <h3 className="font-display flex items-center justify-between text-lg font-semibold text-1">
                  {c.title}
                  <ArrowRight className="h-4 w-4 text-3 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-3">{c.body}</p>
              </Link>
            ))}
          </div>
        )}

        {sections.length > 0 && (
          <div className="mt-12 space-y-10">
            {sections.map((s, i) => (
              <motion.section key={s.heading} variants={reveal} initial="hidden" whileInView="visible"
                viewport={{ once: true, margin: "-60px" }} data-testid={`${testid}-section-${i}`}>
                <h2 className="font-display text-xl font-semibold tracking-tight text-1">{s.heading}</h2>
                {(s.paragraphs || []).map((p, j) => (
                  <p key={j} className="mt-3 text-sm leading-relaxed text-2 lg:text-base">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-3 space-y-2">
                    {s.list.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-2 lg:text-base">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{item}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.section>
            ))}
          </div>
        )}

        {faqs.length > 0 && (
          <div className="mt-12 rounded-2xl border border-line bg-hover px-6" data-testid={`${testid}-faqs`}>
            <Accordion type="single" collapsible>
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                  <AccordionTrigger data-testid={`${testid}-faq-q-${i}`}
                    className="text-left text-sm font-semibold text-1 hover:text-amber-400 lg:text-base">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`${testid}-faq-a-${i}`} className="text-sm leading-relaxed text-2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {contact && (
          <a href={`mailto:${contact.email}`} data-testid={`${testid}-email-cta`}
            className="mt-12 inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black transition-colors hover:bg-amber-400">
            <Mail className="h-4 w-4" /> {contact.cta}
          </a>
        )}
      </div>
    </div>
  );
}

export function HelpCenterPage() {
  return (
    <ContentPage
      testid="help-page"
      eyebrow="Support"
      title="Help Center"
      intro="Everything you need to order, track, pay and return — pick a topic below."
      cards={[
        { title: "Track an Order", body: "Live status for every PAQVERO shipment, straight from our fulfillment network.", to: "/track", testid: "help-card-track" },
        { title: "Shipping & Returns", body: "Delivery zones, timelines, charges and our 30-day return policy.", to: "/shipping-returns", testid: "help-card-shipping" },
        { title: "FAQs", body: "Quick answers on payments, PAQVERO Credit, invoices and more.", to: "/faqs", testid: "help-card-faqs" },
        { title: "Contact Us", body: "Talk to the PAQVERO team — we reply within one business day.", to: "/contact", testid: "help-card-contact" },
      ]}
    />
  );
}

export function ShippingReturnsPage() {
  return (
    <ContentPage
      testid="shipping-returns-page"
      eyebrow="Support"
      title="Shipping & Returns"
      intro="Clear delivery promises across Mumbai's western corridor — and easy returns when something isn't right."
      sections={[
        { heading: "Where we deliver", paragraphs: ["PAQVERO currently serves the Churchgate–Borivali corridor in Mumbai. Enter your PIN code on any product page or at checkout to see your exact delivery estimate and charges before you order."] },
        { heading: "Delivery options", list: ["Standard — 3–5 business days. ₹49, free on orders of ₹1,000 or more (after discounts).", "Express — 1–2 business days for a flat ₹100 premium over Standard.", "Every order is dispatched from the nearest PAQVERO fulfillment center within 24 hours and includes live tracking."] },
        { heading: "Returns", paragraphs: ["30-day easy returns. Unused packaging in original condition gets a full refund to the original payment method — PAQVERO arranges the pickup."], list: ["To request a return, contact PAQVERO Support within 30 days of delivery — email care@paqvero.in or use the Support chat, and our team will review your request and process the return.", "Refunds are processed to the original payment method after pickup and inspection.", "PAQVERO Credit orders are credited back to your credit account."] },
        { heading: "Damaged or wrong items", paragraphs: ["Tell us within 48 hours of delivery with a photo of the issue, and we'll replace the item or refund you — your choice."] },
      ]}
    />
  );
}

const FAQS = [
  { q: "Where does PAQVERO deliver?", a: "We currently serve the Churchgate–Borivali corridor in Mumbai. Check any product page or the checkout PIN box for a live delivery estimate for your PIN code." },
  { q: "How fast is delivery?", a: "Standard delivery takes 3–5 business days and costs ₹49 (free on orders of ₹1,000+ after discounts). Express delivers in 1–2 business days for a flat ₹100 premium. Every order is dispatched within 24 hours." },
  { q: "What payment methods do you accept?", a: "UPI, credit and debit cards, net banking and wallets via Razorpay, Cash on Delivery, and PAQVERO Credit for approved business accounts." },
  { q: "What is PAQVERO Credit?", a: "A 30-day buy-now-pay-later account for approved businesses: order today, pay within 30 days. Apply from your account page — approval is quick, and your dashboard shows every debit, payment and due date." },
  { q: "Do I get an invoice?", a: "Yes — every order gets a GST-ready invoice you can download from the order page or your account. Online orders show a proforma invoice until payment is received, then the final numbered invoice is issued automatically." },
  { q: "How do I track my order?", a: "Use the tracking widget on our homepage with your tracking ID, or open the order from your account's Orders page for the full timeline." },
  { q: "Can I return products?", a: "Yes — 30-day easy returns on unused packaging in original condition, with PAQVERO-arranged pickup and a full refund to the original payment method." },
  { q: "Do you offer bulk or GST billing for businesses?", a: "Invoices carry HSN codes and per-product tax rates ready for GST billing. For large or recurring orders, write to care@paqvero.in and we'll set up pricing for your volumes." },
];

export function FaqsPage() {
  return (
    <ContentPage
      testid="faqs-page"
      eyebrow="Support"
      title="Frequently Asked Questions"
      intro="Quick answers about ordering, delivery, payments and PAQVERO Credit."
      faqs={FAQS}
      contact={{ email: "care@paqvero.in", cta: "Still stuck? Email us" }}
    />
  );
}

export function ContactPage() {
  return (
    <ContentPage
      testid="contact-page"
      eyebrow="Company"
      title="Contact PAQVERO"
      intro="Questions about an order, PAQVERO Credit, or stocking up for your business? We reply within one business day."
      sections={[
        { heading: "Reach us", list: ["Email — care@paqvero.in", "Phone — +91 22 4890 1234 (Mon–Sat, 9 am–7 pm IST)", "Registered office — PAQVERO Supplies Pvt. Ltd., Bandra West, Mumbai 400050"] },
        { heading: "For order issues", paragraphs: ["Include your order number (PQV-…) and, for damaged items, a photo — it helps us resolve things in one reply."] },
      ]}
      contact={{ email: "care@paqvero.in", cta: "Email care@paqvero.in" }}
    />
  );
}

export function CareersPage() {
  return (
    <ContentPage
      testid="careers-page"
      eyebrow="Company"
      title="Careers at PAQVERO"
      intro="We're building the supply layer for Mumbai's businesses — sourcing, stocking and delivering the packaging essentials they run on."
      sections={[
        { heading: "Why PAQVERO", list: ["Small team, real ownership — your work ships to customers the same week.", "Operations meets technology: routing, inventory and pricing systems you can see working in the real world.", "Based in Mumbai, serving the city's busiest business corridor."] },
        { heading: "Open roles", paragraphs: ["We don't have open roles right now, but we always want to meet people who care about logistics, design or engineering. Introduce yourself and we'll reach out when something fits."] },
      ]}
      contact={{ email: "careers@paqvero.com", cta: "Introduce yourself" }}
    />
  );
}

export function NewsroomPage() {
  return (
    <ContentPage
      testid="newsroom-page"
      eyebrow="Company"
      title="Newsroom"
      intro="Updates from PAQVERO — product launches, corridor expansions and company news."
      sections={[
        { heading: "September 2026 — PAQVERO opens its Mumbai storefront", paragraphs: ["Our full catalog of packaging essentials is now live with 24-hour dispatch, live tracking on every order, and PAQVERO Credit for approved businesses."] },
        { heading: "September 2026 — Churchgate–Borivali corridor goes live", paragraphs: ["PIN-level delivery estimates and zone-based charges now cover the full western corridor, with Express delivery in 1–2 business days."] },
        { heading: "Press enquiries", paragraphs: ["Write to press@paqvero.com — we respond within one business day."] },
      ]}
    />
  );
}

export function PrivacyPage() {
  return (
    <ContentPage
      testid="privacy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      intro="Effective September 2026. This policy explains what PAQVERO Supplies Pvt. Ltd. collects and why."
      sections={[
        { heading: "What we collect", list: ["Account details you give us — name, email, phone and delivery addresses.", "Order, payment and credit-account activity needed to fulfil and invoice your purchases.", "Usage data (pages visited, device/browser) that helps us improve the store."] },
        { heading: "How we use it", paragraphs: ["To process orders, payments, deliveries and returns; to operate PAQVERO Credit; to send order confirmations, invoices and delivery updates; and to keep your account secure. We do not sell your personal data."] },
        { heading: "Who we share it with", paragraphs: ["Payment processing (Razorpay), email delivery, and cloud hosting providers — only what's needed to run the service, under their own regulated terms."] },
        { heading: "Your choices", paragraphs: ["You can update account details anytime from your account page, and ask us to delete your account by writing to care@paqvero.in. Transaction records are retained as required by Indian tax law."] },
      ]}
    />
  );
}

export function TermsPage() {
  return (
    <ContentPage
      testid="terms-page"
      eyebrow="Legal"
      title="Terms of Service"
      intro="Effective September 2026. By using PAQVERO you agree to these terms."
      sections={[
        { heading: "Orders and pricing", paragraphs: ["Product prices, stock and delivery estimates are shown at the time of order and confirmed at checkout. An order is accepted when payment is authorised (or, for Cash on Delivery and PAQVERO Credit, when the order is confirmed)."] },
        { heading: "Payments", paragraphs: ["Online payments are processed securely by Razorpay. Cash on Delivery is payable when your order arrives. PAQVERO Credit is a 30-day trade credit facility for approved business accounts — dues unpaid after the due date may suspend the facility."] },
        { heading: "Delivery and returns", paragraphs: ["We deliver within the Churchgate–Borivali corridor on the timelines shown at checkout. Unused packaging in original condition can be returned within 30 days for a full refund to the original payment method."] },
        { heading: "Acceptable use", paragraphs: ["Don't misuse the platform — no fraudulent orders, scraping, or attempts to disrupt the service. We may suspend accounts that violate these terms."] },
        { heading: "Liability", paragraphs: ["Our liability for any order is limited to the amount you paid for it. These terms are governed by the laws of India, with courts in Mumbai having jurisdiction."] },
      ]}
    />
  );
}
